import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, EyeOff, Image, Video, BarChart3, ArrowLeft, LogOut } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import FileUpload from '../components/FileUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAds = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const { authenticated, loading: authLoading, logout, getAuthHeader } = useAdminAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    media_url: '',
    media_type: 'image',
    click_url: '',
    placement: [],
    priority: 5,
    status: 'active'
  });

  const placements = ['shop', 'blog', 'gallery', 'all'];

  useEffect(() => {
    if (!authLoading && !authenticated) {
      navigate('/admin/login');
    }
  }, [authenticated, authLoading, navigate]);

  useEffect(() => {
    if (authenticated) {
      fetchAds();
    }
  }, [authenticated]);

  const fetchAds = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/advertisements`, {
        params: { status: '' },
        headers: getAuthHeader()
      });
      setAds(response.data.advertisements || []);
    } catch (error) {
      console.error('Error fetching ads:', error);
      toast.error('Failed to load advertisements');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePlacementChange = (placement) => {
    setFormData(prev => {
      const currentPlacements = Array.isArray(prev.placement) ? prev.placement : [];
      if (currentPlacements.includes(placement)) {
        return { ...prev, placement: currentPlacements.filter(p => p !== placement) };
      } else {
        return { ...prev, placement: [...currentPlacements, placement] };
      }
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      media_url: '',
      media_type: 'image',
      click_url: '',
      placement: [],
      priority: 5,
      status: 'active'
    });
    setMediaFiles([]);
    setEditingAd(null);
    setIsEditing(false);
  };

  const handleEdit = (ad) => {
    setFormData({
      title: ad.title || '',
      description: ad.description || '',
      media_url: ad.media_url || '',
      media_type: ad.media_type || 'image',
      click_url: ad.click_url || '',
      placement: Array.isArray(ad.placement) ? ad.placement : [ad.placement],
      priority: ad.priority || 5,
      status: ad.status || 'active'
    });
    // Set media file if exists
    if (ad.media_url) {
      setMediaFiles([{
        id: 'existing',
        url: ad.media_url,
        media_type: ad.media_type || 'image',
        is_primary: true
      }]);
    } else {
      setMediaFiles([]);
    }
    setEditingAd(ad);
    setIsEditing(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const mediaUrl = mediaFiles.length > 0 ? mediaFiles[0].url : formData.media_url;
    const mediaType = mediaFiles.length > 0 ? mediaFiles[0].media_type : formData.media_type;
    
    if (!formData.title || !mediaUrl) {
      toast.error('Title and Media are required');
      return;
    }

    try {
      const submitData = {
        ...formData,
        media_url: mediaUrl,
        media_type: mediaType
      };
      
      if (editingAd) {
        await axios.put(`${API}/admin/advertisements/${editingAd._id}`, submitData, {
          headers: getAuthHeader()
        });
        toast.success('Advertisement updated successfully');
      } else {
        await axios.post(`${API}/admin/advertisements`, submitData, {
          headers: getAuthHeader()
        });
        toast.success('Advertisement created successfully');
      }
      resetForm();
      fetchAds();
    } catch (error) {
      console.error('Error saving ad:', error);
      toast.error('Failed to save advertisement');
    }
  };

  const handleDelete = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this advertisement?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/advertisements/${adId}`, {
        headers: getAuthHeader()
      });
      toast.success('Advertisement deleted successfully');
      fetchAds();
    } catch (error) {
      console.error('Error deleting ad:', error);
      toast.error('Failed to delete advertisement');
    }
  };

  const toggleStatus = async (ad) => {
    try {
      const newStatus = ad.status === 'active' ? 'inactive' : 'active';
      await axios.put(`${API}/admin/advertisements/${ad._id}`, { status: newStatus }, {
        headers: getAuthHeader()
      });
      toast.success(`Advertisement ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchAds();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update status');
    }
  };

  if (authLoading || !authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Advertisement Management</h1>
          <p className="text-sm text-gray-500">Create, edit, and manage advertisements</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {editingAd ? <Pencil className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
                  {editingAd ? 'Edit Advertisement' : 'Create New Advertisement'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <Input
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <Input
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Media (Image or Video) *</label>
                    <FileUpload
                      category="ads"
                      files={mediaFiles}
                      onChange={setMediaFiles}
                      multiple={false}
                      allowVideo={true}
                      showPrimary={false}
                      sortable={false}
                      label="Upload Media"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Click URL</label>
                    <Input
                      name="click_url"
                      value={formData.click_url}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Placement</label>
                    <div className="flex flex-wrap gap-2">
                      {placements.map(p => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => handlePlacementChange(p)}
                          className={`px-3 py-1 text-sm rounded-full transition-colors ${
                            formData.placement.includes(p)
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority (1-10, higher = shown first)
                    </label>
                    <Input
                      type="number"
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      min="1"
                      max="10"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button type="submit" className="flex-1">
                      {editingAd ? 'Update' : 'Create'} Ad
                    </Button>
                    {editingAd && (
                      <Button type="button" variant="outline" onClick={resetForm}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Ads List Section */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  All Advertisements ({ads.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                    <p className="mt-2 text-gray-600">Loading ads...</p>
                  </div>
                ) : ads.length === 0 ? (
                  <div className="text-center py-8 text-gray-600">
                    No advertisements found. Create your first ad!
                  </div>
                ) : (
                  <div className="space-y-4">
                    {ads.map((ad) => (
                      <div
                        key={ad._id}
                        className={`border rounded-lg p-4 ${
                          ad.status === 'active' ? 'bg-white' : 'bg-gray-50 opacity-70'
                        }`}
                      >
                        <div className="flex gap-4">
                          <div className="w-32 h-24 flex-shrink-0 rounded overflow-hidden bg-gray-100">
                            {ad.media_type === 'video' ? (
                              <video
                                src={ad.media_url}
                                className="w-full h-full object-cover"
                                muted
                              />
                            ) : (
                              <img
                                src={ad.media_url}
                                alt={ad.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                          </div>

                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-semibold text-gray-900">{ad.title}</h3>
                                <p className="text-sm text-gray-600 mt-1">{ad.description}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleStatus(ad)}
                                  className={`p-2 rounded-full transition-colors ${
                                    ad.status === 'active'
                                      ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                  title={ad.status === 'active' ? 'Deactivate' : 'Activate'}
                                >
                                  {ad.status === 'active' ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                </button>
                                <button
                                  onClick={() => handleEdit(ad)}
                                  className="p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                                  title="Edit"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(ad._id)}
                                  className="p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
                              <span className="flex items-center gap-1 text-gray-500">
                                {ad.media_type === 'video' ? <Video className="h-3 w-3" /> : <Image className="h-3 w-3" />}
                                {ad.media_type}
                              </span>
                              <span className="text-gray-500">
                                Priority: {ad.priority}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                ad.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                              }`}>
                                {ad.status}
                              </span>
                            </div>

                            <div className="flex flex-wrap gap-1 mt-2">
                              {(Array.isArray(ad.placement) ? ad.placement : [ad.placement]).map(p => (
                                <span
                                  key={p}
                                  className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                                >
                                  {p}
                                </span>
                              ))}
                            </div>

                            <div className="flex gap-4 mt-2 text-xs text-gray-500">
                              <span>👁 {ad.impressions || 0} impressions</span>
                              <span>🖱 {ad.clicks || 0} clicks</span>
                              {ad.impressions > 0 && (
                                <span>
                                  CTR: {((ad.clicks / ad.impressions) * 100).toFixed(1)}%
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
    </div>
  );
};

export default AdminAds;
